/**
 * Rascunho de checkout no navegador (não é pedido).
 * Usa localStorage com TTL para sobreviver a fechar a aba;
 * pedidos continuam só no Firebase (STORAGE GUARD).
 *
 * Não grava: CPF, senha, cartão, tokens.
 */

import type { CartItem } from '../types';

const CART_KEY = 'eamb_cart_v1';
const ADDRESS_KEY = 'eamb_checkout_address_v1';
const DEFAULT_MAX_AGE_MS = 4 * 60 * 60 * 1000; // 4h

export type CheckoutAddressDraft = {
  deliveryReference: string;
  addressStreet: string;
  addressNumber: string;
  addressDistrict: string;
  addressComplement: string;
  addressCep: string;
  customerName: string;
  customerPhone: string;
  customerObservations: string;
};

export type CartSessionDraft = {
  items: CartItem[];
  vendorId: string | null;
  savedAt: number;
};

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Remove chaves legadas de sessionStorage (migração one-shot). */
function clearLegacySessionKeys(): void {
  try {
    sessionStorage.removeItem(CART_KEY);
    sessionStorage.removeItem(ADDRESS_KEY);
    sessionStorage.removeItem('cartItems');
  } catch {
    /* ignore */
  }
}

function readJson<T>(key: string): T | null {
  const store = storage();
  if (!store) return null;
  try {
    const raw = store.getItem(key);
    if (!raw) {
      // fallback: sessionStorage antigo
      const legacy = sessionStorage.getItem(key);
      if (!legacy) return null;
      store.setItem(key, legacy);
      sessionStorage.removeItem(key);
      return JSON.parse(legacy) as T;
    }
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  const store = storage();
  if (!store) return;
  try {
    store.setItem(key, JSON.stringify(value));
    clearLegacySessionKeys();
  } catch {
    /* private mode / quota */
  }
}

function removeKey(key: string): void {
  const store = storage();
  try {
    store?.removeItem(key);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/** Só campos necessários do item — evita inflar storage e vazamento de metadados. */
function sanitizeCartItem(item: CartItem): CartItem {
  return {
    ...item,
    // mantém shape CartItem; sem extras sensíveis no projeto atual
  };
}

export function saveCartSession(items: CartItem[], vendorId: string | null): void {
  if (!items.length) {
    removeKey(CART_KEY);
    return;
  }
  const draft: CartSessionDraft = {
    items: items.map(sanitizeCartItem),
    vendorId,
    savedAt: Date.now(),
  };
  writeJson(CART_KEY, draft);
}

export function loadCartSession(maxAgeMs = DEFAULT_MAX_AGE_MS): CartSessionDraft | null {
  const draft = readJson<CartSessionDraft>(CART_KEY);
  if (!draft?.items?.length) return null;
  if (typeof draft.savedAt === 'number' && Date.now() - draft.savedAt > maxAgeMs) {
    removeKey(CART_KEY);
    return null;
  }
  return draft;
}

export function clearCartSession(): void {
  removeKey(CART_KEY);
}

export function saveAddressDraft(draft: CheckoutAddressDraft): void {
  const hasData = Object.values(draft).some((v) => String(v || '').trim().length > 0);
  if (!hasData) {
    removeKey(ADDRESS_KEY);
    return;
  }
  writeJson(ADDRESS_KEY, { ...draft, savedAt: Date.now() });
}

export function loadAddressDraft(maxAgeMs = DEFAULT_MAX_AGE_MS): CheckoutAddressDraft | null {
  const raw = readJson<CheckoutAddressDraft & { savedAt?: number }>(ADDRESS_KEY);
  if (!raw) return null;
  if (typeof raw.savedAt === 'number' && Date.now() - raw.savedAt > maxAgeMs) {
    removeKey(ADDRESS_KEY);
    return null;
  }
  const { savedAt: _s, ...draft } = raw;
  return draft;
}

export function clearAddressDraft(): void {
  removeKey(ADDRESS_KEY);
}
